import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Steps,
  Upload,
} from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import ProductInfoTabs from "../../components/Products/InfoTabs";
import { createProduct, updateProduct } from "../../repository/productApi";
import {
  generateInternalReference,
  generateRandomHSN,
} from "../../utils/utils";
import "./addProducts.css";

const { TextArea } = Input;
const { Option } = Select;

const ProductForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.data || null;
  const editMode = location.state?.editMode || false;
  const [form] = Form.useForm();
  const [current, setCurrent] = useState(0);
  const auth = JSON.parse(localStorage.getItem("auth"));
  const org = auth?.org;
  const onChange = (value) => {
    
    setCurrent(value);
  };

  useEffect(() => {
    if (editMode && editData) {
      // Map photo URL to AntD Upload file format
      const fileList = editData.Photo
        ? [
            {
              uid: "-1", // unique id
              name: "Product Photo",
              status: "done",
              url: editData.Photo,
            },
          ]
        : [];

      form.setFieldsValue({
        ...editData,
        CanBeSold: editData.CanBeSold,
        CanBePurchased: editData.CanBePurchased,
        Photo: fileList,
      });
    }
  }, [editMode, editData, form]);

  const handleSubmit = async (values) => {
    const photoFile = values.Photo?.[0]?.originFileObj || null;

    const formData = new FormData();
    if (photoFile) formData.append("Photo", photoFile);
    formData.append("org", org);

    Object.keys(values).forEach((key) => {
      if (key !== "Photo") formData.append(key, values[key]);
    });

    try {
      message.loading({
        content: editMode ? "Updating product..." : "Creating product...",
        key: "productAction",
      });

      let response;

      if (editMode && editData?.id) {
        // 🔄 Update existing product
        response = await updateProduct(editData.id, formData);
      } else {
        // ➕ Create new product
        response = await createProduct(formData);
      }

      message.success({
        content: editMode
          ? "Product updated successfully!"
          : "Product created successfully!",
        key: "productAction",
        duration: 2,
      });

      form.resetFields();
      navigate("/pos/products");
    } catch (error) {
      console.error("Error saving product:", error);

      message.error({
        content:
          error.response?.data?.message ||
          (editMode ? "Failed to update product" : "Failed to create product"),
        key: "productAction",
        duration: 3,
      });
    }
  };

  return (
    <div className="d-flex" style={{ height: "100vh" }}>
      {/* Sidebar and Header */}
      <Header />

      <div style={{ flex: "11 11 100%", padding: "20px", marginTop: "64px" }}>
        <div className="row px-5">
          <h3 style={{ padding: 0, fontWeight: "600" }}>
            {editMode ? "Edit Product" : "Product"}
          </h3>
          <Row style={{ zoom: "0.8", margin: "0", padding: "0" }}>
            <Col md={24} className="mb-3">
              <Card className="card">
                <Row>
                  <Col md={10}>
                    <div className="gap-3 pt-2">
                      <Button
                        type="primary" // for styling
                        onClick={() => form.submit()} // triggers onFinish
                        className="db w-25 btn-secondary py-4"
                      >
                        Save
                      </Button>
                    </div>
                  </Col>
                  <Col md={10}></Col>
                  <Col md={4}>
                    <Steps
                      type="navigation"
                      current={current}
                      onChange={onChange}
                      className="site-navigation-steps"
                      items={[
                        {
                          status: "finish",
                          title: "Draft",
                        },
                        {
                          status: "process",
                          title: "Posted",
                        },
                      ]}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
          <div className="col-12 r-shadow p-4 card">
            <Form
              encType="multipart/form-data"
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                Photo: [],
                ProductName: "Test Product",
                CustomerTaxes: 1,
                VendorTaxes: 1,
                CanBeSold: false,
                CanBePurchased: true,
                ProductType: "Service",
                InternalReference: generateInternalReference(),
                BarCode: "",
                HSN_SAC_Code: generateRandomHSN(),
                HSN_SAC_Description: "Test HSN/SAC Description",
                SalesPrice: 1500,
                Cost: 0,
                InternalNotes: "Test Notes",
                Quantity: 0,
                Discount: 0,
              }}
            >
              <Form.Item noStyle>
                <Row
                  justify="space-between"
                  align="middle"
                  style={{ marginBottom: 20 }}
                >
                  {/* <Col>
                    <Button type="primary" htmlType="submit" className="xb">
                      Save
                    </Button>
                  </Col> */}
                </Row>
              </Form.Item>
              {/* Top row fields */}
              <Row gutter={16}>
                <Col md={12}>
                  <Form.Item
                    name="ProductName"
                    rules={[
                      {
                        required: true,
                        message: "Please enter the product name",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter product name"
                      style={{
                        height: "50px",
                        borderLeft: "0",
                        borderTop: "0",
                        borderRight: "0",
                        paddingLeft: "0",
                        borderBottom: "2px solid #1890ff",
                        borderRadius: 0,
                        boxShadow: "none",
                        fontSize: "16px",
                        color: "#333",
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col md={12}>
                  <Form.Item
                    label="Photo"
                    name="Photo"
                    valuePropName="fileList" // <-- ensures fileList is passed to form
                    getValueFromEvent={(e) =>
                      Array.isArray(e) ? e : e?.fileList
                    } // normalize value
                  >
                    <Upload
                      listType="picture"
                      maxCount={1}
                      beforeUpload={() => false} // stop auto-upload
                    >
                      <Button icon={<UploadOutlined />}>Upload Photo</Button>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>

              {/* Tabs for other product info */}
              <Row>
                <Col md={24}>
                  {/* Pass form down to tabs so inner fields stay connected */}
                  <ProductInfoTabs form={form} />
                </Col>
              </Row>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductForm;
